import { getInvoiceSeller } from '../constants/invoiceSeller';
import { getInvoiceCountryLabel } from './buildOrderInvoiceData';

/** @typedef {import('../constants/invoiceSeller').InvoiceSeller} InvoiceSeller */

const FISCAL_KEYS = [
  'nombreFiscal',
  'nif',
  'direccion',
  'ciudad',
  'provincia',
  'codigoPostal',
  'pais',
  'emailFacturacion',
  'telefonoFacturacion',
];

/**
 * @param {string | undefined} value
 * @returns {string}
 */
function asTrimmedString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * @param {object | null | undefined} payload
 * @returns {object | null}
 */
function unwrapFiscalPayload(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return null;
  }
  if (Object.hasOwn(payload, 'datosFiscalesAcademia')) {
    const nested = payload.datosFiscalesAcademia;
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      return nested;
    }
  }
  if (Object.hasOwn(payload, 'emisorFactura')) {
    const nested = payload.emisorFactura;
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      return nested;
    }
  }
  return payload;
}

/**
 * @param {object | null | undefined} payload
 * @returns {Record<string, string> | null}
 */
export function pickAcademyFiscalFields(payload) {
  const source = unwrapFiscalPayload(payload);
  if (!source) {
    return null;
  }

  /** @type {Record<string, string>} */
  const picked = {};
  for (const key of FISCAL_KEYS) {
    if (!Object.hasOwn(source, key)) {
      continue;
    }
    const value = source[key];
    if (typeof value === 'string') {
      picked[key] = value;
    }
  }
  return picked;
}

const IDENTITY_FISCAL_KEYS = ['nombreFiscal', 'nif', 'direccion'];

/**
 * @param {object | null | undefined} payload
 * @returns {boolean}
 */
export function hasConfiguredFiscalData(payload) {
  const picked = pickAcademyFiscalFields(payload);
  if (!picked) {
    return false;
  }
  return IDENTITY_FISCAL_KEYS.some((key) => asTrimmedString(picked[key]));
}

/**
 * @param {string} country
 * @param {string} fallback
 * @returns {string}
 */
function resolveFiscalCountry(country, fallback) {
  const trimmed = asTrimmedString(country);
  if (!trimmed) {
    return fallback;
  }
  if (/^[A-Za-z]{2}$/.test(trimmed)) {
    return getInvoiceCountryLabel(trimmed) || trimmed.toUpperCase();
  }
  return trimmed;
}

/**
 * Maps academy fiscal settings onto the invoice issuer, keeping env defaults for empty fields.
 * @param {Record<string, string> | null | undefined} fiscalData
 * @param {InvoiceSeller} [defaults]
 * @returns {InvoiceSeller}
 */
export function mapAcademyFiscalDataToInvoiceSeller(fiscalData, defaults = getInvoiceSeller()) {
  const source = fiscalData && typeof fiscalData === 'object' ? fiscalData : {};

  return {
    legalName: asTrimmedString(source.nombreFiscal) || defaults.legalName,
    taxId: asTrimmedString(source.nif) || defaults.taxId,
    addressLine: asTrimmedString(source.direccion) || defaults.addressLine,
    city: asTrimmedString(source.ciudad) || defaults.city,
    province: asTrimmedString(source.provincia) || defaults.province || '',
    postalCode: asTrimmedString(source.codigoPostal) || defaults.postalCode,
    country: resolveFiscalCountry(source.pais, defaults.country),
    email: asTrimmedString(source.emailFacturacion) || defaults.email,
    phone: asTrimmedString(source.telefonoFacturacion) || defaults.phone,
  };
}
